import { Injectable } from '@angular/core';
import { filter, forEach, isEmpty } from 'lodash';
import { timer, BehaviorSubject, Observable } from 'rxjs';
import { CoreModalService } from '@app/modules/core-modal/services';
import {
  TOAST_FADE_IN_TIME,
  TOAST_FADE_OUT_TIME,
} from '@app/modules/toast/component/animations/toast.animation';
import { ToastParams, ToastType } from '@shared/types';
import { I18n } from '@app/i18n/strings-keys';

@Injectable({ providedIn: 'root' })
export class ToastService {
  public static readonly DEFAULT_TOAST_TIMER: number = 3000;
  public toasts$: Observable<ToastParams[]>;

  private toastsQueue = new BehaviorSubject<ToastParams[]>([]);
  private toastsNotInQueue: ToastParams[] = [];

  public constructor(private coreModalService: CoreModalService) {
    this.toasts$ = this.toastsQueue.asObservable();
    this.coreModalService.hasOpenedModals$().subscribe((value: boolean) => {
      if (!value) {
        this.resetTimers();
      }
    });
  }

  public processMessages(toastParams: ToastParams[]): void {
    if (isEmpty(toastParams)) {
      return;
    }
    forEach(toastParams, (toastParam) => {
      this.addToast({ ...toastParam });
    });
  }

  public async removeToast(toast: ToastParams): Promise<void> {
    this.dequeue(toast);

    await timer(TOAST_FADE_OUT_TIME).toPromise();

    this.enqueuePendingToast();

    this.setNewToastTimerSubscription();
  }

  public getToastTitle(toastType?: ToastType): string {
    let title: string;
    switch (toastType) {
      case ToastType.SUCCESS:
        title = I18n.CommonMessages.Success;
        break;
      default:
        title = I18n.CommonMessages.NotFound;
    }

    return title;
  }

  private dequeue(toast: ToastParams): void {
    if (toast.timerSubscription) {
      toast.timerSubscription.unsubscribe();
    }

    this.toastsQueue.next(
      filter(this.toastsQueue.getValue(), (t) => t.id !== toast.id),
    );
  }

  private enqueuePendingToast(): void {
    const pendingToast = this.toastsNotInQueue.shift();

    if (pendingToast) {
      this.addToast(pendingToast);
    }
  }

  private setNewToastTimerSubscription(): void {
    const openToasts = this.toastsQueue.getValue();

    if (openToasts.length) {
      const [firstToast] = openToasts;
      if (!firstToast.timerSubscription) {
        this.updateTimer(firstToast, false);
      }
    }
  }

  private updateTimer(toast: ToastParams, waitForFadeIn: boolean): void {
    let timerValue = toast.timer;
    if (waitForFadeIn) {
      timerValue += TOAST_FADE_IN_TIME;
    }
    if (toast.closeOnTimerOrButton) {
      toast.timerSubscription = timer(timerValue).subscribe(() => {
        this.removeToast(toast);
      });
    }
  }

  private resetTimers(): void {
    forEach(this.toastsQueue.getValue(), (toast) => {
      if (!toast.timerSubscription) {
        return;
      }
      toast.timerSubscription.unsubscribe();
      this.updateTimer(toast, false);
    });
  }

  private addToast(toast: ToastParams): void {
    if (this.toastsQueue.getValue().length < 3) {
      toast.timer = this.getToastDuration(toast);
      this.toastsQueue.next([...this.toastsQueue.getValue(), toast]);
      if (this.toastsQueue.getValue().length === 1) {
        this.updateTimer(toast, true);
      }
    } else {
      this.toastsNotInQueue.push(toast);
    }
  }

  private getToastDuration(toast: ToastParams): number {
    const toastTitle = this.getToastTitle(toast.level);
    const message = `${toastTitle} ${toast.message}`;
    return Math.min(Math.max(message.length * 50, 2000), 7000);
  }
}
